import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Image, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { app } from "../firebase";
import AnimatedECardPreview, { getECardAssetType, getECardMediaType, getECardPlaybackAssetUrl, normalizeAnimatedTextLayers } from "./RegistryWidgets/AnimatedECardPreview";

const ECardViewer = () => {
	const route = useRoute();
	const navigation = useNavigation();
	const deliveryId = route?.params?.deliveryId;
	const [delivery, setDelivery] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const isEmailGifRender = Platform.OS === "web" && typeof window !== "undefined" &&
		new URLSearchParams(window.location.search).get("render") === "email-gif";

	useEffect(() => {
		let isMounted = true;

		const loadDelivery = async () => {
			if (!deliveryId) {
				setError("This eCard link is missing its delivery code.");
				setLoading(false);
				return;
			}

			try {
				const snap = await getDoc(doc(getFirestore(app), "ecardDeliveries", deliveryId));
				if (!isMounted) return;

				if (!snap.exists()) {
					setError("We could not find this eCard.");
					setDelivery(null);
				} else {
					setDelivery({ id: snap.id, ...snap.data() });
					setError("");
				}
			} catch (loadError) {
				if (!isMounted) return;
				setError(loadError.message || "Unable to load this eCard.");
			} finally {
				if (isMounted) setLoading(false);
			}
		};

		loadDelivery();
		return () => {
			isMounted = false;
		};
	}, [deliveryId]);

	const card = useMemo(() => {
		if (!delivery) return null;
		return {
			title: "Animated graduation eCard",
			cardName: "Animated graduation eCard",
			assetUrl: getECardPlaybackAssetUrl(delivery) || delivery.cardImageUrl || "",
			videoAssetUrl: delivery.videoAssetUrl || "",
			assetType: getECardAssetType(delivery),
			mediaType: getECardMediaType(delivery),
			textLayers: Array.isArray(delivery.animatedTextLayers) ? delivery.animatedTextLayers : [],
		};
	}, [delivery]);

	const layers = useMemo(() => normalizeAnimatedTextLayers(card || {}), [card]);
	const isStatic = card?.assetUrl && !String(delivery?.cardType || "").includes("animated");
	const isVideo = getECardMediaType(card || {}) === "video";

	return (
		<View style={isEmailGifRender ? styles.renderPage : styles.page}>
			{!isEmailGifRender && (
				<View style={styles.header}>
					<Pressable style={styles.logoButton} onPress={() => navigation.navigate("My Tassel - Welcome")}>
						<Text style={styles.logoText}>MyTassel</Text>
					</Pressable>
				</View>
			)}

			<View style={isEmailGifRender ? styles.renderMain : styles.main}>
				{loading ? (
					<View style={styles.statePanel}>
						<ActivityIndicator color="#4c956c" />
						<Text style={styles.stateText}>Loading your eCard...</Text>
					</View>
				) : error ? (
					<View style={styles.statePanel}>
						<Text style={styles.errorTitle}>eCard unavailable</Text>
						<Text style={styles.stateText}>{error}</Text>
					</View>
				) : (
					<>
						{!isEmailGifRender && (
							<View style={styles.titleBlock}>
								<Text style={styles.eyebrow}>A graduation eCard from {delivery.senderName || "someone special"}</Text>
							</View>
						)}

						<View style={styles.stage}>
							{isStatic ? (
								<Image source={{ uri: card.assetUrl }} style={styles.staticImage} resizeMode="contain" />
							) : (
								<AnimatedECardPreview
									card={card}
									layerTextValues={delivery.animatedLayerTextValues || {}}
									muted={isEmailGifRender ? true : false}
									controls={isEmailGifRender ? false : true}
									autoPlay={false}
									loop={false}
									style={isEmailGifRender ? styles.renderAnimatedCard : styles.animatedCard}
									assetStyle={isEmailGifRender ? styles.renderAnimatedAsset : styles.animatedAsset}
								/>
							)}
						</View>

						{!isEmailGifRender && delivery.message ? (
							<View style={styles.messagePanel}>
								<Text style={styles.messageLabel}>Message</Text>
								<Text style={styles.messageText}>{delivery.message}</Text>
							</View>
						) : null}

						{!isEmailGifRender && layers.length > 0 && isVideo ? (
							<Text style={styles.helperText}>Use the video controls to watch the personalized animation.</Text>
						) : null}
					</>
				)}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	page: {
		minHeight: "100vh",
		backgroundColor: "#f8fafc",
	},
	renderPage: {
		width: 520,
		height: 520,
		backgroundColor: "#111827",
		overflow: "hidden",
	},
	header: {
		height: 64,
		paddingHorizontal: 24,
		alignItems: "flex-start",
		justifyContent: "center",
		borderBottomWidth: 1,
		borderBottomColor: "#e5e7eb",
		backgroundColor: "#ffffff",
	},
	logoButton: {
		paddingVertical: 8,
		paddingRight: 12,
	},
	logoText: {
		fontSize: 20,
		fontWeight: "800",
		color: "#2f6f4e",
	},
	main: {
		width: "100%",
		maxWidth: 960,
		alignSelf: "center",
		paddingHorizontal: 20,
		paddingVertical: 28,
		alignItems: "center",
	},
	renderMain: {
		width: 520,
		height: 520,
		padding: 0,
		margin: 0,
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#111827",
		overflow: "hidden",
	},
	titleBlock: {
		width: "100%",
		alignItems: "center",
		marginBottom: 18,
	},
	eyebrow: {
		fontSize: 14,
		fontWeight: "700",
		color: "#4b5563",
		textTransform: "uppercase",
		textAlign: "center",
	},
	stage: {
		width: "100%",
		alignItems: "center",
		justifyContent: "center",
	},
	animatedCard: {
		width: "100%",
		maxWidth: 760,
		aspectRatio: 1,
		borderRadius: 10,
		backgroundColor: "#111827",
		boxShadow: "0 16px 42px rgba(15, 23, 42, 0.18)",
	},
	renderAnimatedCard: {
		width: 520,
		height: 520,
		aspectRatio: 1,
		borderRadius: 0,
		backgroundColor: "#111827",
		boxShadow: "none",
	},
	animatedAsset: {
		borderRadius: 10,
		maxHeight: 640,
	},
	renderAnimatedAsset: {
		borderRadius: 0,
		maxHeight: 520,
	},
	staticImage: {
		width: "100%",
		maxWidth: 720,
		aspectRatio: 1.4,
		borderRadius: 10,
		backgroundColor: "#111827",
	},
	messagePanel: {
		width: "100%",
		maxWidth: 760,
		marginTop: 18,
		padding: 18,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: "#e5e7eb",
		backgroundColor: "#ffffff",
	},
	messageLabel: {
		fontSize: 13,
		fontWeight: "800",
		color: "#4b5563",
		textTransform: "uppercase",
	},
	messageText: {
		marginTop: 8,
		fontSize: 18,
		lineHeight: 26,
		color: "#111827",
	},
	helperText: {
		marginTop: 14,
		fontSize: 14,
		lineHeight: 20,
		color: "#6b7280",
		textAlign: "center",
	},
	statePanel: {
		minHeight: 320,
		alignItems: "center",
		justifyContent: "center",
	},
	stateText: {
		marginTop: 12,
		fontSize: 16,
		color: "#4b5563",
		textAlign: "center",
	},
	errorTitle: {
		fontSize: 28,
		fontWeight: "800",
		color: "#111827",
		textAlign: "center",
	},
});

export default ECardViewer;
